"use client";

import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FieldValues, UseFormReturn } from "react-hook-form";
import { clearSSOToasts } from "@/components/SSOAuthButton/hooks/useSSOState";

import { Form } from "@/components/Form";
import { Typography } from "@/components/Typography";
import { Button } from "@/components/Button";
import { SSOAuthContainer } from "../SSOAuthButton";

import { registerValidation } from "@/utils/validators/register";
import { ApiErrorMessageBuilder } from "@/utils/apiErrorHandler";

import { getSiteInfo } from "@/sites";

import { getCookie } from "@/services/CookiesService";

import { useToast } from "@/hooks/ui/useToast";
import { useRegister } from "@/hooks/auth/useRegister";
import { pushSignUpCompletedEvent } from "@/utils/gtm";
import { PAGES } from "@/internals/constants/pages";
import { NEXT_LOCALE } from "@/internals/constants/cookies";
import { RegisterFormProps } from "./types";
import { FormValidationMethods } from "@/components/Form/types";
import { QR_REFERENCE_COOKIE } from "@/containers/AuthContainer/types";
import { API_ERROR_FALLBACKS } from "@/internals/constants/errorCodes";
import { SsoUserDataExtractor } from "@/services/Auth/ssoUserDataExtractor";
import { SSO_PROVIDER, type SsoCompletedMeta, type SsoProviderId } from "../SSOAuthButton/types";

export default function Register({
  onSetFormType,
  onRegister,
  setIsLoading,
  isLoading,
  source,
  registerData,
}: RegisterFormProps) {
  const { toast } = useToast();
  const { t } = useTranslation(PAGES.REGISTER);

  const { onHandleRegister, registerResponse, registerError } = useRegister();
  const [isSsoLoading, setIsSsoLoading] = useState(false);

  const [password, setPassword] = useState("");
  const [formObject, setFormObject] = useState<
    { values: FieldValues; methods: UseFormReturn } | undefined
  >(undefined);

  const hasTrackedSignup = useRef(false);
  useEffect(() => {
    clearSSOToasts();
    hasTrackedSignup.current = false;
  }, []);

  const schema = registerValidation(registerData, password);

  const {
    richtext: { Tag, content, className },
    button: { id, label },
  } = registerData.authModeSwitcher;

  const onRegisterSubmit = async (values: FieldValues, methods: UseFormReturn) => {
    if (isLoading) return;
    hasTrackedSignup.current = false;
    setIsLoading(true);
    setFormObject({ values, methods });
    onHandleRegister({
      email: values.email,
      password: values.password,
      reference: getCookie(QR_REFERENCE_COOKIE),
      locale: getCookie(NEXT_LOCALE),
    });
  };

  const onSsoCompleted = useCallback(
    (provider: SsoProviderId, payload: unknown, meta?: SsoCompletedMeta) => {
      const userData = SsoUserDataExtractor.extractUserData(provider, payload, meta);
      if (!userData) {
        void payload;
        return;
      }

      if (!hasTrackedSignup.current) {
        hasTrackedSignup.current = true;
        pushSignUpCompletedEvent({
          userId: userData.userId,
          email: userData.email,
          source,
          ssoProvider: provider,
        });
      }

      onRegister({ provider });
    },
    [source, onRegister]
  );

  useEffect(() => {
    if (!formObject) return;
    if (registerResponse?.status === 200 || registerResponse?.status === 201) {
      if (!hasTrackedSignup.current) {
        hasTrackedSignup.current = true;
        pushSignUpCompletedEvent({
          userId: registerResponse?.data?.userId || "unknown",
          email: formObject.values?.email || "unknown",
          source,
        });
      }
      setIsLoading(true);
      onRegister(formObject.values, formObject.methods);
      setFormObject(undefined);
    }
  }, [registerResponse]);

  useEffect(() => {
    if (!formObject) return;

    const status = registerError?.status || registerError?.statusCode;
    if (status === 400 || status === 401 || status === 404) {
      formObject.methods.setError("email", {
        message: registerData.invalidAuthMessage,
      });
      formObject.methods.resetField("password", { defaultValue: "" });
    } else {
      const errorMessage = ApiErrorMessageBuilder.buildToastMessage(registerError, {
        fallbackMessage: API_ERROR_FALLBACKS.AUTH.REGISTER,
      });

      toast({ message: errorMessage, type: "error" });
    }
    setIsLoading(false);
  }, [registerError]);

  const checkPassword = (value: string, methods: FormValidationMethods) => {
    setPassword(value);
    methods.trigger("password");
  };

  const handleLoginClick = () => {
    // Clear SSO toasts before switching forms
    clearSSOToasts();
    onSetFormType("login");
  };

  const { roundedAuthButtons } = getSiteInfo(process.env.NEXT_PUBLIC_DOMAIN!);

  return (
    <div
      className="Register"
      data-testid="Register"
    >
      <Typography
        Tag="p"
        classname="Register__title"
        size="xl"
        spacing={5}
      >
        {registerData.title}
      </Typography>
      <SSOAuthContainer
        mode="signup"
        rounded={roundedAuthButtons}
        isFormLoading={isLoading}
        labels={{
          [SSO_PROVIDER.GOOGLE]: {
            label: t("google_button_label") as string,
            loading: t("sso_button_loading_label") as string,
          },
          [SSO_PROVIDER.MICROSOFT]: {
            label: t("microsoft_button_label") as string,
            loading: t("sso_button_loading_label") as string,
          },
          [SSO_PROVIDER.APPLE]: {
            label: t("apple_button_label") as string,
            loading: t("sso_button_loading_label") as string,
          },
        }}
        errors={{
          registerError: registerData.invalidAuthSSOMessage,
        }}
        onSsoLoadingChange={setIsSsoLoading}
        onSsoCompleted={onSsoCompleted}
      />
      <Form
        fields={registerData.formFields}
        onSubmit={onRegisterSubmit}
        schema={schema}
        validationMode="onSubmit"
        isDisabled={isSsoLoading}
        isLoading={isLoading}
        onWatchFields={({ field, value, methods }) => {
          if (field === "password") checkPassword(value, methods);
        }}
        isAuth
      />

      <div className="Auth__switcher">
        <Typography
          Tag={Tag}
          spacing={2}
          classname={className}
        >
          {content}
        </Typography>
        {/* //TODO: components-lib: This button will be replace by a Link Component */}
        <Button
          ghost
          spacing={2}
          buttonId={id}
          label={label}
          disabled={isSsoLoading}
          dataQA="register-modal-login-button"
          onClick={handleLoginClick}
          className="switch-form-button"
        />
      </div>
      <div className="register-disclaimer">
        <span dangerouslySetInnerHTML={{ __html: registerData.disclaimer }}></span>
      </div>
    </div>
  );
}
